import { supabase } from '../lib/supabase';

// Module-level cache for subscription_plans (TTL: 5 minutes)
let _plansCache = null;
let _plansCacheTs = 0;
const PLANS_CACHE_TTL = 5 * 60 * 1000;

async function fetchPlans() {
  const now = Date.now();
  if (_plansCache && now - _plansCacheTs < PLANS_CACHE_TTL) return _plansCache;
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('max_projects, max_storage, name, billing_cycle')
    .eq('is_active', true);
  if (!error && data) {
    _plansCache = data;
    _plansCacheTs = now;
  }
  return data || [];
}

/**
 * Fetch max_projects and max_storage for a given tier from subscription_plans table.
 * Returns { maxProjects: number|null, maxStorage: number|null }
 * null = unlimited
 */
export async function getPlanLimitsForTier(tier, userId) {
  try {
    // For team_member tier, fetch limits from team_members table
    if ((tier || '').toLowerCase() === 'team_member' && userId) {
      const { data: teamRow } = await supabase
        .from('team_members')
        .select('max_projects, max_storage')
        .eq('member_user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (teamRow) {
        return {
          maxProjects: teamRow.max_projects ?? null,
          maxStorage: teamRow.max_storage ?? null,
        };
      }
    }

    const data = await fetchPlans();

    if (!data?.length) return { maxProjects: null, maxStorage: null };

    const lowerTier = (tier || 'free').toLowerCase();

    const match = data.find((p) => {
      const name = (p.name || '').toLowerCase();
      if (lowerTier === 'free') return name.includes('free');
      return name.includes(lowerTier) && p.billing_cycle === 'monthly';
    });

    return {
      maxProjects: match?.max_projects ?? null,
      maxStorage: match?.max_storage ?? null,
    };
  } catch {
    return { maxProjects: null, maxStorage: null };
  }
}

/**
 * Check if user can create a new project.
 * Returns { allowed: boolean, current: number, max: number|null }
 *
 * Team logic (same as credits):
 *   - team owner  → checks total team count vs owner's plan limit
 *   - team member → checks member's own count vs member cap AND total team count vs owner's plan limit
 */
export async function checkProjectLimit(userId, tier) {
  const lowerTier = (tier || '').toLowerCase();

  // ── Team owner ──
  if (lowerTier === 'team') {
    const { maxProjects } = await getPlanLimitsForTier('team', userId);
    if (maxProjects === null) return { allowed: true, current: 0, max: null };
    const { data: teamTotal } = await supabase.rpc('get_team_total_project_count');
    const current = teamTotal ?? 0;
    return { allowed: current < maxProjects, current, max: maxProjects };
  }

  // ── Team member ──
  if (lowerTier === 'team_member') {
    const { maxProjects: memberCap } = await getPlanLimitsForTier('team_member', userId);

    // Check member's own cap first
    if (memberCap !== null) {
      const { count: memberCount } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);
      const memberCurrent = memberCount ?? 0;
      if (memberCurrent >= memberCap) {
        return { allowed: false, current: memberCurrent, max: memberCap };
      }
    }

    // Check owner's pool via DB function
    const { data: teamRow } = await supabase
      .from('team_members')
      .select('team_owner_id')
      .eq('member_user_id', userId)
      .eq('is_active', true)
      .maybeSingle();
    if (!teamRow?.team_owner_id) return { allowed: false, current: 0, max: 0 };

    const { maxProjects: ownerMax } = await getPlanLimitsForTier('team', teamRow.team_owner_id);
    if (ownerMax === null) return { allowed: true, current: 0, max: memberCap };

    const { data: teamTotal } = await supabase.rpc('get_team_total_project_count');
    const current = teamTotal ?? 0;
    return { allowed: current < ownerMax, current, max: ownerMax };
  }

  // ── Regular user ──
  const { maxProjects } = await getPlanLimitsForTier(tier, userId);
  if (maxProjects === null) return { allowed: true, current: 0, max: null };

  const { count, error } = await supabase
    .from('projects')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);

  const current = error ? 0 : (count || 0);
  return { allowed: current < maxProjects, current, max: maxProjects };
}

/**
 * Check if user can use storage.
 * Returns { allowed: boolean, current: number, max: number|null }
 *
 * Team logic (same as credits):
 *   - team owner  → checks total team count vs owner's plan limit
 *   - team member → checks member's own count vs member cap AND total team count vs owner's plan limit
 */
export async function checkSaveDesignLimit(userId, tier) {
  const lowerTier = (tier || '').toLowerCase();

  // ── Team owner ──
  if (lowerTier === 'team') {
    const { maxStorage } = await getPlanLimitsForTier('team', userId);
    if (maxStorage === null) return { allowed: true, current: 0, max: null };
    const [{ data: teamTotal }, { data: uploadCount }] = await Promise.all([
      supabase.rpc('get_team_total_save_design_count'),
      supabase.rpc('count_uploaded_media', { target_user_id: userId }),
    ]);
    const current = (teamTotal ?? 0) + (uploadCount ?? 0);
    return { allowed: current < maxStorage, current, max: maxStorage };
  }

  // ── Team member ──
  if (lowerTier === 'team_member') {
    const { maxStorage: memberCap } = await getPlanLimitsForTier('team_member', userId);

    // Check member's own cap first (saved_designs + uploaded_media)
    if (memberCap !== null) {
      const [{ count: memberCount }, { data: memberUpload }] = await Promise.all([
        supabase.from('saved_designs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.rpc('count_uploaded_media', { target_user_id: userId }),
      ]);
      const memberCurrent = (memberCount ?? 0) + (memberUpload ?? 0);
      if (memberCurrent >= memberCap) {
        return { allowed: false, current: memberCurrent, max: memberCap };
      }
    }

    // Check owner's pool via DB function
    const { data: teamRow } = await supabase
      .from('team_members')
      .select('team_owner_id')
      .eq('member_user_id', userId)
      .eq('is_active', true)
      .maybeSingle();
    if (!teamRow?.team_owner_id) return { allowed: false, current: 0, max: 0 };

    const { maxStorage: ownerMax } = await getPlanLimitsForTier('team', teamRow.team_owner_id);
    if (ownerMax === null) return { allowed: true, current: 0, max: memberCap };

    const [{ data: teamTotal }, { data: ownerUpload }] = await Promise.all([
      supabase.rpc('get_team_total_save_design_count'),
      supabase.rpc('count_uploaded_media', { target_user_id: teamRow.team_owner_id }),
    ]);
    const current = (teamTotal ?? 0) + (ownerUpload ?? 0);
    return { allowed: current < ownerMax, current, max: ownerMax };
  }

  // ── Regular user ──
  const { maxStorage } = await getPlanLimitsForTier(tier, userId);
  if (maxStorage === null) return { allowed: true, current: 0, max: null };

  const [{ count, error }, { data: uploadCount }] = await Promise.all([
    supabase.from('saved_designs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.rpc('count_uploaded_media', { target_user_id: userId }),
  ]);

  const current = (error ? 0 : (count || 0)) + (uploadCount ?? 0);
  return { allowed: current < maxStorage, current, max: maxStorage };
}
